import JoinSheet from '@/components/Auth/JoinSheet';
import { Button } from 'design-system';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SetRelationshipModal from '../../../pages/FamilyTree/SetRelationshipModal';
import { extractFirstName } from '../../../lib/name';
import './Grave.css';

const GraveButton = ({ grave, onGraveUpdated = () => {} }) => {
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [openRelationDialog, setOpenRelationDialog] = useState(false);

  const isAuth = useSelector((state) => state.auth.isAuthorized);

  const firstName = useMemo(() => extractFirstName(grave?.name), [grave?.name]);

  const hasCurrentRelation = Boolean(grave?.current_user_relation);

  const goto = () => {
    navigate(`/graves/${grave.slug}`);
  };

  const handleSetRelation = () => {
    if (!isAuth) {
      setOpenModal(true);
    } else {
      setOpenRelationDialog(true);
    }
  };

  const mergeGraveUpdates = (base, updates = {}, graveUser = null) => {
    if (!base && !updates) return null;
    const merged = {
      ...base,
      ...(updates || {}),
    };

    const baseKey = base?.key;
    if (baseKey && !merged.key) {
      merged.key = baseKey;
    }

    let nextUsers = null;
    if (Array.isArray(updates?.grave_users)) {
      nextUsers = [...updates.grave_users];
    } else if (Array.isArray(base?.grave_users)) {
      nextUsers = [...base.grave_users];
    }

    if (graveUser) {
      if (!nextUsers) {
        nextUsers = [];
      }
      const matchIndex = nextUsers.findIndex((item) => {
        if (!item) return false;
        if (item.id && graveUser.id && item.id === graveUser.id) return true;
        const itemUserId = item.user?.id || item.user_id;
        const graveUserId = graveUser.user?.id || graveUser.user_id;
        return itemUserId && graveUserId ? itemUserId === graveUserId : false;
      });

      if (matchIndex >= 0) {
        nextUsers[matchIndex] = graveUser;
      } else {
        nextUsers.unshift(graveUser);
      }

      merged.current_user_relation = graveUser;
    } else if (updates?.current_user_relation) {
      merged.current_user_relation = updates.current_user_relation;
    }

    if (nextUsers) {
      merged.grave_users = nextUsers;
    }

    return merged;
  };

  return (
    <>
      <div className="grave-ctaButtons">
        {!hasCurrentRelation && (
          <Button
            style={{ flexGrow: 1 }}
            onClick={handleSetRelation}
            type="primary"
            size="small"
            shape="round"
          >
            <FormattedMessage
              id="connect_with_grave"
              defaultMessage="Set relation"
              values={{ graveName: firstName }}
            />
          </Button>
        )}
        <Button
          onClick={goto}
          type={hasCurrentRelation ? 'primary' : 'default'}
          size="small"
          shape="round"
          style={{ width: hasCurrentRelation ? '100%' : 'auto', flexGrow: 1 }}
        >
          <FormattedMessage id="search_grave_visit_cta" defaultMessage="Visit grave" />
        </Button>
      </div>

      {!hasCurrentRelation && (
        <p className="grave-ctaHint">
          <FormattedMessage
            id="search_grave_no_relation_hint"
            values={{ firstName }}
            defaultMessage="You can set your relation to {firstName}."
          />
        </p>
      )}
      {openModal && (
        <JoinSheet
          open={openModal}
          onOpenChange={setOpenModal}
          subjectName={grave?.name}
          reloadOnAuth={true}
        />
      )}

      {openRelationDialog && (
        <SetRelationshipModal
          visible={openRelationDialog}
          onClose={() => setOpenRelationDialog(false)}
          initialGrave={grave}
          action={'relation'}
          onActionCompleted={({ grave: updatedGrave, graveUser }) => {
            if (typeof onGraveUpdated !== 'function') return;

            if (!updatedGrave && !grave) return;

            const relationSource = updatedGrave?.current_user_relation || graveUser || null;
            const next = mergeGraveUpdates(grave, updatedGrave, relationSource);
            if (next) {
              onGraveUpdated(next);
            }
          }}
          onRelationConfirmed={() => {}}
          personSearchEnabled={false}
        />
      )}
    </>
  );
};

export default GraveButton;
